import { type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import {
  LogIn,
  Ruler,
  LayoutDashboard,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { Logo, VectorMark, VECTOR_TAGLINE } from './Logo';
import { Button, Card, CardBody, Spinner } from '../ui';

/**
 * Lightweight auth gate. Feature pages require a signed-in session (real
 * Cognito, obtained via the OAuth Authorization Code + PKCE flow) because they
 * call the real Cognito-authed backend.
 *
 * Only the OAuth callback/logout handlers are PUBLIC so a user can complete the
 * sign-in round-trip before a session exists; every other route (including
 * Settings) requires authentication.
 */
const PUBLIC_PREFIXES = ['/global/auth/'];

function isPublicPath(pathname: string): boolean {
  return PUBLIC_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(p),
  );
}

/** The three product value props shown on the sign-in brand panel. */
const VALUE_PROPS: { icon: LucideIcon; title: string; body: string }[] = [
  {
    icon: Ruler,
    title: 'Estimate against your real architecture',
    body: 'Ground effort in your services, packages, and past work — not guesswork.',
  },
  {
    icon: LayoutDashboard,
    title: 'A sprint board with intelligence underneath',
    body: 'Plan, track, and complete sprints with insights surfaced as you go.',
  },
  {
    icon: Sparkles,
    title: 'Ask Vector anything about your team',
    body: 'Catch up, dig into decisions, and get answers from your own context.',
  },
];

/**
 * The unauthenticated sign-in experience: a branded two-column layout (single
 * column on mobile) with a brand/value-prop panel and a clean sign-in card.
 *
 * Purely presentational — the only auth affordance is {@link useAuth}'s
 * `signIn` (Midway/AmazonFederate OAuth Authorization Code + PKCE).
 */
function SignInPrompt() {
  const { signIn } = useAuth();

  return (
    <section
      aria-labelledby="signin-heading"
      className="animate-fade-in mx-auto grid max-w-5xl items-stretch gap-6 py-6 lg:grid-cols-2 lg:gap-8 lg:py-14"
    >
      {/* Brand panel */}
      <div className="relative overflow-hidden rounded-2xl border border-surface-border bg-surface-1 p-8 lg:p-10">
        {/* Subtle gradient backdrop + accent glow. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-gradient-to-br from-accent/20 via-transparent to-transparent"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-accent/20 blur-3xl"
        />
        <div className="relative flex h-full flex-col">
          <Logo size="lg" />
          <div className="mt-8">
            <h1 className="text-2xl font-semibold tracking-tight text-zinc-50 lg:text-3xl">
              Plan, estimate, ship.
            </h1>
            <p className="mt-2 max-w-sm text-sm text-zinc-400">
              {VECTOR_TAGLINE}
            </p>
          </div>
          <ul className="mt-8 space-y-5">
            {VALUE_PROPS.map(({ icon: Icon, title, body }) => (
              <li key={title} className="flex gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent-soft text-accent-fg">
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </span>
                <div>
                  <p className="text-sm font-medium text-zinc-100">{title}</p>
                  <p className="mt-0.5 text-xs leading-relaxed text-zinc-500">
                    {body}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Sign-in card */}
      <div className="flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardBody className="flex flex-col gap-6 p-8">
            <div className="flex flex-col items-center text-center">
              <VectorMark className="h-12 w-12" />
              <h2
                id="signin-heading"
                className="mt-4 text-lg font-semibold text-zinc-50"
              >
                Welcome to Vector
              </h2>
              <p className="mt-1.5 text-sm text-zinc-400">
                Sign in with your Amazon (Midway) account to plan work,
                estimate against your architecture, and ask Vector anything.
              </p>
            </div>

            <Button
              className="w-full"
              size="lg"
              leftIcon={<LogIn className="h-4 w-4" />}
              onClick={signIn}
            >
              Sign in with Midway
            </Button>

            <p className="text-center text-[11px] leading-relaxed text-zinc-600">
              This app talks to the real backend, which requires an
              AmazonFederate (Midway) sign-in.
            </p>
          </CardBody>
        </Card>
      </div>
    </section>
  );
}

/** Gates its children behind an authenticated session (public routes bypass). */
export function AuthGate({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  if (isPublicPath(location.pathname)) {
    return <>{children}</>;
  }
  if (status === 'loading') {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner label="Checking session" />
      </div>
    );
  }
  if (status === 'unauthenticated') {
    return <SignInPrompt />;
  }
  return <>{children}</>;
}

export default AuthGate;
